/**
 * Сторонняя веб-аналитика публичного лендинга: GTM, GA4, Яндекс.Метрика.
 *
 * GA4 и Метрика поднимаются отсюда, а не сниппетом в `<head>`: у нас SPA с
 * клиентской маршрутизацией, переходы между `/` и `/business` сниппет не видит —
 * их отдаёт `web-analytics-router.ts`.
 *
 * GTM — исключение: его официальный сниппет стоит в `index.html` под такой же
 * проверкой хоста. Контейнер должен быть виден в исходнике страницы (проверки
 * установки со стороны маркетинга и Google читают HTML, а не бандл) и должен
 * подниматься до бандла — иначе GTM Preview подключается с задержкой, а
 * слушатель кликов контейнера пропускает первые действия на странице. Здесь от
 * GTM остаётся только `dataLayer`: события воронки кладёт `trackEvent`.
 *
 * Идентификаторы захардкожены: это не секреты, номер счётчика виден в любом
 * клиентском коде. А вот включаются они только на продовом хосте — см.
 * `trackedHostnames` и ручное включение для стейджа рядом с ним.
 *
 * `<noscript>`-части официальных сниппетов сознательно опущены: без JS страница
 * вообще не отрисовывается, и фиксировать такой «просмотр» бессмысленно. Для
 * сниппета GTM причина сильнее — `<noscript>`-половина грузит контейнер в обход
 * любой проверки, и stage отчитывался бы в продовый контейнер.
 *
 * PII: сюда не передаётся ничего из формы заявки — только путь страницы. Правило
 * из `features/apply/analytics.ts` действует и здесь.
 */

/** Функция-приёмник Метрики: до загрузки tag.js складывает вызовы в очередь `a`. */
type YandexMetrikaFn = ((
  counterId: number,
  action: string,
  ...rest: ReadonlyArray<unknown>
) => void) & {
  a?: Array<Array<unknown>>;
  l?: number;
};

/** Приёмник команд GA4 — тот же, что объявляет официальный сниппет. */
type GtagFn = (...args: ReadonlyArray<unknown>) => void;

declare global {
  interface Window {
    dataLayer?: Array<unknown>;
    gtag?: GtagFn;
    ym?: YandexMetrikaFn;
  }
}

export interface WebAnalyticsConfig {
  /**
   * Контейнер Google Tag Manager, например `GTM-XXXXXXX`. Отсюда не грузится —
   * сниппет стоит в `index.html`. Здесь это признак «контейнер на странице»: по
   * нему `trackEvent` решает, класть ли событие в `dataLayer`.
   */
  readonly gtmId?: string;
  /**
   * Поток данных GA4, например `G-XXXXXXXXXX`. Нужен только когда GA4
   * подключается напрямую, без GTM: если задан и `gtmId`, счётчик получит
   * просмотр дважды.
   */
  readonly ga4Id?: string;
  /** Номер счётчика Яндекс.Метрики. */
  readonly ymCounterId?: string;
}

/**
 * Идентификаторы счётчиков money2me.
 *
 * GA4 подключён напрямую, а не тегом внутри контейнера GTM: так вся проводка
 * видна в репозитории и покрыта тестами, а просмотры и воронка попадают в отчёты
 * без единого триггера в чужом кабинете.
 *
 * Цена решения — требование к контейнеру: тега GA4 на `G-J2QB6X6LPQ` в нём быть
 * не должно, два подключения одного потока считают каждый просмотр дважды. Сам
 * контейнер GTM остаётся на странице — он нужен маркетингу под собственные теги,
 * и события воронки уходят в том числе в `dataLayer`.
 *
 * `gtmId` продублирован в сниппете `index.html`, который и грузит контейнер;
 * расхождение ловит `web-analytics-snippet.test.ts`.
 */
export const webAnalyticsIds: WebAnalyticsConfig = {
  gtmId: 'GTM-W83CQX23',
  ga4Id: 'G-J2QB6X6LPQ',
  ymCounterId: '111924359',
};

/**
 * Хосты, на которых счётчики включены. Список закрытый: незнакомый хост —
 * локальная разработка, превью или stage — остаётся без аналитики.
 *
 * Отказ по умолчанию выбран сознательно. Продовую статистику нельзя очистить
 * задним числом, а Метрика умеет только исключить хост фильтром, но не пометить
 * его: раз протёкший stage-трафик остаётся в отчётах навсегда. Цена ошибки в
 * другую сторону — прод без данных — обнаруживается за минуту в кабинете.
 *
 * Прод и stage разведены одной приставкой (`promo.` против `s-promo.`), поэтому
 * список точный, без сопоставления по маске: опечатка в маске включила бы stage.
 * Если лендинг начнут отдавать ещё с одного адреса, его нужно добавить сюда —
 * иначе он останется без аналитики.
 */
const trackedHostnames: ReadonlySet<string> = new Set(['promo.moneyto.me']);

function isTrackedHost(hostname: string): boolean {
  return trackedHostnames.has(hostname.toLowerCase());
}

/**
 * Ручное включение счётчиков вне прода: `?analytics=on` поднимает их на текущей
 * вкладке, `?analytics=off` выключает обратно.
 *
 * Без этого стейдж проверить нечем: счётчики там не грузятся вообще, а GTM
 * Preview, реалтайм Метрики и GA4 DebugView работают только на живой странице.
 * Признак ручной и явный, поэтому случайный посетитель стейджа, бот или CI его
 * не включат — в отчёты попадают только осознанные тестовые сессии.
 */
const overrideStorageKey = 'moneyto:web-analytics';

type AnalyticsOverride = 'on' | 'off';

function readStoredOverride(): AnalyticsOverride | undefined {
  try {
    const stored = sessionStorage.getItem(overrideStorageKey);
    return stored === 'on' || stored === 'off' ? stored : undefined;
  } catch {
    // Приватный режим или заблокированное хранилище: признак проживёт до
    // перезагрузки, в пределах одной загрузки это ничего не меняет.
    return undefined;
  }
}

function storeOverride(value: AnalyticsOverride): void {
  try {
    sessionStorage.setItem(overrideStorageKey, value);
  } catch {
    // См. выше.
  }
}

function resolveOverride(search: string): AnalyticsOverride | undefined {
  const requested = new URLSearchParams(search).get('analytics');
  if (requested !== 'on' && requested !== 'off') return readStoredOverride();
  storeOverride(requested);
  return requested;
}

/**
 * Конфигурация для текущего хоста: счётчики на проде, пустой набор на всех
 * остальных хостах. Ручной признак сильнее хоста в обе стороны — он же даёт
 * своим людям способ не попадать в продовую статистику.
 */
export function resolveWebAnalyticsConfig(
  hostname: string,
  search: string = location.search,
): WebAnalyticsConfig {
  const override = resolveOverride(search);
  if (override !== undefined) return override === 'on' ? webAnalyticsIds : {};
  return isTrackedHost(hostname) ? webAnalyticsIds : {};
}

/** Разобранная конфигурация: только включённые провайдеры. */
interface ResolvedConfig {
  readonly gtmId?: string;
  readonly ga4Id?: string;
  readonly ymCounterId?: number;
}

let active: ResolvedConfig = {};
let initialised = false;

function appendScript(src: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  const first = document.getElementsByTagName('script')[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
}

function dataLayer(): Array<unknown> {
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

/**
 * Команда gtag в очередь `dataLayer`.
 *
 * Кладётся именно объект `arguments`, как в официальном сниппете: gtag.js
 * распознаёт команды только по нему, а обычный массив молча игнорирует. С
 * массивом счётчик грузится и выглядит рабочим, но `config` не выполняется и в
 * GA4 не уходит ни одного запроса — так и было на стенде 2026-08-27.
 */
const gtag: GtagFn = function gtag() {
  dataLayer().push(arguments);
};

function initGa4(ga4Id: string): void {
  // Глобальный `gtag` объявляет и официальный сниппет: по нему тег находят
  // теги контейнера, сторонние скрипты и отладчики вроде Tag Assistant.
  window.gtag = gtag;
  appendScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Id)}`);
  gtag('js', new Date());
  gtag('config', ga4Id);
}

function initYandexMetrika(counterId: number): void {
  if (!window.ym) {
    const queue: Array<Array<unknown>> = [];
    const stub: YandexMetrikaFn = (...args) => {
      queue.push(args);
    };
    stub.a = queue;
    stub.l = Date.now();
    window.ym = stub;
  }
  // `?id=` — как в сниппете из кабинета: по нему Метрика узнаёт свой скрипт
  // (в том числе в собственной проверке установки счётчика).
  appendScript(`https://mc.yandex.ru/metrika/tag.js?id=${counterId}`);
  // Параметры — как в сниппете из кабинета счётчика. Вебвизор пишет сессии, но
  // ввод в форму заявки закрыт классом `ym-disable-keys` на самой форме
  // (`features/apply/apply-modal.tsx`), а не настройкой в кабинете.
  window.ym(counterId, 'init', {
    ssr: true,
    webvisor: true,
    clickmap: true,
    // Зарезервировано: ecommerce-данные в `dataLayer` пока никто не кладёт.
    ecommerce: 'dataLayer',
    referrer: document.referrer,
    url: location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  });
}

/**
 * Подключает включённые трекеры. Повторный вызов игнорируется: сниппеты не
 * идемпотентны, а HMR и тесты дёргают точку входа не один раз.
 */
export function initWebAnalytics(config: WebAnalyticsConfig): void {
  if (initialised) return;
  initialised = true;

  const gtmId = config.gtmId || undefined;
  const ga4Id = config.ga4Id || undefined;
  const ymCounterId = config.ymCounterId ? Number(config.ymCounterId) : undefined;
  active = { gtmId, ga4Id, ymCounterId };

  // `gtmId` здесь ничего не грузит: контейнер уже поднял сниппет из
  // `index.html` — на том же наборе хостов, поэтому признак сходится с ним.
  if (ga4Id) initGa4(ga4Id);
  if (ymCounterId !== undefined) initYandexMetrika(ymCounterId);
}

/** Просмотр страницы: полный адрес, откуда пришли и заголовок. */
export interface PageView {
  /** Полный адрес открытой страницы — с query, иначе теряются utm-метки. */
  readonly url: string;
  /** Адрес предыдущей страницы: по нему строятся отчёты о переходах внутри сайта. */
  readonly referrer: string;
  /** Заголовок страницы: без него в отчётах остаётся голый адрес. */
  readonly title: string;
}

/**
 * Просмотр страницы при клиентской навигации — только для Метрики.
 *
 * Она отправляет хит на `init` и переходов через History API не видит. GA4 такие
 * переходы считает сам (enhanced measurement, включено по умолчанию), поэтому
 * свой `page_view` мы не шлём: два источника одного просмотра дали бы двойной
 * счёт. В `dataLayer` просмотры тоже не кладём — без триггера в контейнере push
 * никуда не уходит, а с триггером удвоил бы отчёт GA4.
 */
export function trackPageView(view: PageView): void {
  if (active.ymCounterId === undefined) return;
  // `referer` с одной «r» — так параметр называется в API Метрики.
  window.ym?.(active.ymCounterId, 'hit', view.url, {
    referer: view.referrer,
    title: view.title,
  });
}

/**
 * Свойства события: только категориальные значения. Пользовательский ввод сюда
 * не попадает — правило PII из `features/apply/analytics.ts` действует и здесь.
 */
export type AnalyticsEventProps = Readonly<Record<string, string | number | boolean>>;

/**
 * Событие воронки. Одно имя на три системы: событие `dataLayer` для GTM, имя
 * события GA4 и идентификатор цели Метрики совпадают — иначе отчёты в разных
 * кабинетах не сверить.
 *
 * Само по себе событие ничего не отправляет: в GTM на каждое имя нужен триггер
 * Custom Event и тег GA4 Event, в Метрике — цель «JavaScript-событие» с тем же
 * идентификатором. Без них вызов тихо уходит в никуда, ошибки не будет.
 */
export function trackEvent(event: string, props?: AnalyticsEventProps): void {
  if (active.gtmId) dataLayer().push({ event, ...props });
  if (active.ga4Id) gtag('event', event, props);
  if (active.ymCounterId !== undefined) window.ym?.(active.ymCounterId, 'reachGoal', event, props);
}

/** Сброс состояния модуля — для тестов. */
export function resetWebAnalytics(): void {
  active = {};
  initialised = false;
}
